import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { submitRequest } from './submitRequest';
import {
  VectorAnalyzeLogsRequest,
  VectorAnalyzeLogsResponse,
  VectorCheckConfigParams,
  VectorCheckConfigResponse,
  VectorDatadogLogsResponse,
  VectorDatadogServicesResponse,
  VectorDeleteConfigPayload,
  VectorDeleteConfigResponse,
  VectorListConfigsResponse,
  VectorPreviewYamlPayload,
  VectorPreviewYamlResponse,
  VectorSubmitPayload,
  VectorSubmitResponse,
  VectorValidateYamlPayload,
  VectorValidateYamlResponse,
} from '@mercedes-benz/pmas-common';

export class VectorApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(discoveryApi: DiscoveryApi, fetchApi: FetchApi) {
    this.discoveryApi = discoveryApi;
    this.fetchApi = fetchApi;
  }

  async checkConfig(
    params: VectorCheckConfigParams,
  ): Promise<VectorCheckConfigResponse> {
    const query = new URLSearchParams({
      tenant: params.tenant,
      cluster: params.cluster,
      environment: params.environment,
      configType: params.configType ?? 'logs',
    });
    if (params.region) {
      query.set('region', params.region);
    }

    return submitRequest({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
      method: 'GET',
      path: `/vector/check-config?${query.toString()}`,
    });
  }

  async submitConfig(
    payload: VectorSubmitPayload,
  ): Promise<VectorSubmitResponse> {
    return submitRequest({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
      method: 'POST',
      path: '/vector/submit',
      body: payload,
    });
  }

  async getDatadogLogs(params: {
    tenantId: string;
    from?: string;
    to?: string;
    services?: string;
    limit?: number;
  }): Promise<VectorDatadogLogsResponse> {
    const query = new URLSearchParams({
      from: params.from ?? 'now-24h',
      to: params.to ?? 'now',
      limit: String(params.limit ?? 300),
    });

    if (params.services) {
      query.set('services', params.services);
    }

    return submitRequest({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
      method: 'GET',
      path: `/vector/datadog/logs/${params.tenantId}?${query.toString()}`,
    });
  }

  async getDatadogServices(params: {
    tenantId: string;
    from?: string;
    to?: string;
  }): Promise<VectorDatadogServicesResponse> {
    const query = new URLSearchParams({
      from: params.from ?? 'now-24h',
      to: params.to ?? 'now',
    });

    return submitRequest({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
      method: 'GET',
      path: `/vector/datadog/services/${params.tenantId}?${query.toString()}`,
    });
  }

  async analyzeLogs(
    payload: VectorAnalyzeLogsRequest,
  ): Promise<VectorAnalyzeLogsResponse> {
    return submitRequest({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
      method: 'POST',
      path: '/vector/analyze-logs',
      body: payload,
    });
  }

  async listConfigs(tenants: string[]): Promise<VectorListConfigsResponse> {
    const query = new URLSearchParams({
      tenants: tenants.join(','),
    });

    return submitRequest({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
      method: 'GET',
      path: `/vector/list-configs?${query.toString()}`,
    });
  }

  async previewYaml(
    payload: VectorPreviewYamlPayload,
    signal?: AbortSignal,
  ): Promise<VectorPreviewYamlResponse> {
    return submitRequest({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
      method: 'POST',
      path: '/vector/preview-yaml',
      body: payload,
      signal,
    });
  }

  async validateYaml(
    payload: VectorValidateYamlPayload,
    signal?: AbortSignal,
  ): Promise<VectorValidateYamlResponse> {
    return submitRequest({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
      method: 'POST',
      path: '/vector/validate-yaml',
      body: payload,
      signal,
    });
  }

  async deleteConfig(
    payload: VectorDeleteConfigPayload,
  ): Promise<VectorDeleteConfigResponse> {
    return submitRequest({
      discoveryApi: this.discoveryApi,
      fetchApi: this.fetchApi,
      method: 'POST',
      path: '/vector/delete-config',
      body: payload,
    });
  }
}
